/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import {
  AfterViewInit, Component, EventEmitter,
  HostListener, Input, OnChanges, OnInit,
  Output, SimpleChanges,
} from '@angular/core';

import Swiper from 'swiper';

import { generateObjectId } from '../exports';
import { SwiperImageCarouselConfiguration } from './config';
import { ImageCarouselSlide } from './image-carousel-slide.model';

/*
 * Component for Swiper Image Carousel.
 */
@Component({
  selector: 'bulb-image-carousel',
  templateUrl: './image-carousel.component.html',
  standalone: false,
})
export class ImageCarouselComponent implements OnInit, OnChanges, AfterViewInit {
  /**
   * Slides for the carousel with images and caption.
   */
  @Input() public slides: Array<ImageCarouselSlide> = new Array<ImageCarouselSlide>();

  /**
   * Orientation of carousel.
   */
  @Input() orientation: string;

  /**
   * If the page is editable.
   */
  @Input() editable: boolean;

  /**
   * block id of the carousel.
   */
  @Input() public blockId: string;

  /**
   * Type of editor.
   */
  @Input() source: string;

  /**
   * Effect type of carousel.
   */
  @Input() effect?: 'slide' | 'fade' | 'cube' | 'coverflow' | 'flip';

  /**
   * Event emitter for adding image to the carousel.
   */
  @Output() public imageAdded: EventEmitter<number> = new EventEmitter<number>();

  /**
   * Event emitter for removing image from the carousel.
   */
  @Output() public imageRemoved: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event emitter for changing active index of swiper.
   */
  @Output() public changeActiveIndex: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Id for the swiper container.
   */
  public swiperContainerId: string;

  /**
   * Reference object of the swiper instance.
   */
  private swiper: Swiper;

  /**
   * Current configuration of swiper.
   */
  swiperConfigRef : any;

  /**
   * Whether we should set the custom height to the swiper or not.
   */
  public customHeight: boolean = false;

  /**
   * Maximum height among all carousel images.
   */
  public maxHeight: number;

  /**
   * Transition speed among all carousel images.
   */
  private transitionSpeed: number = 1000;

  /**
   * Default maximum height of the carousel.
   */
  private static CAROUSEL_MAX_HEIGHT : number = 445;

  /**
   * transition type cube.
   */
  private TRANSITION_EFFECT_CUBE: string = 'cube';

  /**
   * Default configurations for carousel autoplay.
   */
  private static AUTOPLAY_CONFIGS : object = {
    delay: 4000,
    disableOnInteraction: true,
    pauseOnMouseEnter: false,
  };

  /**
   * Listen to the event that image carousel slide added on the server.
   */
  @HostListener('window:imageCarouselSlideAdded', ['$event'])
  onImageCarouselSlideAdded(event) {
    if (event.detail.contentBlock.id === this.blockId) {
      $('body').css('overflow', 'auto');
      this.swiper.removeAllSlides();
      this.updateCarouselSlides(event);
      this.swiper.slideTo(event.detail.currentIndex + 1);
    }
  }

  /**
   * Listen to the event that image carousel slide removed on the server.
   */
  @HostListener('window:imageCarouselSlideRemoved', ['$event'])
  onImageCarouselSlideRemoved(event) {
    if (event.detail.contentBlock.id === this.blockId) {
      this.swiper.slideTo(event.detail.currentIndex - 1);
      this.updateCarouselSlides(event);
    }
  }

  /**
   * Listen to the event that image carousel crop is complete.
   */
  @HostListener('window:enableSliding', ['$event'])
  onEnableSliding(event) {
    if (event.detail.slides !== null) {
      if (event.detail.contentBlock && event.detail.contentBlock.id === this.blockId) {
        this.updateCarouselSlides(event);
      }
    }
    this.swiper.allowSlideNext = true;
    this.swiper.allowSlidePrev = true;
  }

  /**
   * Listen to the event that image carousel crop is started and disable sliding.
   */
  @HostListener('window:disableSliding', ['$event'])
  onDisableSliding() {
    this.swiper.allowSlideNext = false;
    this.swiper.allowSlidePrev = false;
  }

  /**
   * Listen to the event that image carousel slides are updated.
   */
  @HostListener('window:updateCarousel', ['$event'])
  onUpdateCarousel(event) {
    if (event.detail.contentBlock.id === this.blockId) {
      this.updateCarouselSlides(event);
    }
  }

  /**
   * Listen to the event for dispatching current active slide index of selected Carousel.
   */
  @HostListener('window:currentSlideIndex', ['$event'])
  onCurrentSlideIndex(event) {
    if (event.detail.blockId === this.blockId) {
      const currentSlideIndex = this.swiper.activeIndex;
      const eventName = 'dispatchSlideIndex';
      const customSlideIndexEvent = new CustomEvent(eventName, {
        detail: {
          activeIndex: currentSlideIndex,
        },
      });
      window.dispatchEvent(customSlideIndexEvent);
    }
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit() {
    // generating a unique id for the swiper container.
    this.swiperContainerId = `swiper-container-${generateObjectId()}`;
    if (this.slides) {
      this.getMaxHeight(this.slides);
      document.body.style.overflow = 'auto';
    }
  }

  /**
   *
   * A lifecycle hook that is called after any data-bound properties is changed of a directive.
   * To handle any additional changes to the component.
   *
   * @param changes in the component
   */
  ngOnChanges(changes: SimpleChanges) {
    if (this.swiper && changes.slides && changes.slides.currentValue) {
      this.swiper.slideTo(this.swiper.activeIndex + 1);
    }
  }

  /**
   * A lifecycle hook that is called after view initialized.
   */
  ngAfterViewInit(): void {
    // Adding the configuration for the swiper.
    SwiperImageCarouselConfiguration.simulateTouch = true;
    SwiperImageCarouselConfiguration.loop = !this.editable;
    SwiperImageCarouselConfiguration.autoplay = false;
    SwiperImageCarouselConfiguration.speed = this.transitionSpeed;
    if (!this.editable) {
      SwiperImageCarouselConfiguration.autoplay = ImageCarouselComponent.AUTOPLAY_CONFIGS;
    }
    SwiperImageCarouselConfiguration.effect = this.effect || 'slide';
    this.swiper = new Swiper(`#${this.swiperContainerId}`, SwiperImageCarouselConfiguration);
    this.updateSwiperActiveIndex();
    this.toggleClass(this.effect);
    this.changeActiveIndex.emit(this.swiperConfigRef.activeIndex);
  }

  /**
   * get active index value on change.
   */
  updateSwiperActiveIndex() {
    const that = this;
    this.swiperConfigRef = this.swiper.on('activeIndexChange', (swiper) => {
      that.emitIndex(swiper.activeIndex);
      return swiper;
    });
  }

  /**
   * To emit the active index.
   *
   * @param activeIndex current active index value.
   */
  emitIndex(activeIndex) {
    this.changeActiveIndex.emit(activeIndex);
  }

  /**
   * Remove the image from the carousel.
   */
  removeSlide() {
    this.slides.splice(this.swiper.realIndex, 1);
    this.swiper.removeSlide(this.swiper.realIndex);
    this.swiper.update();
  }

  /**
   * It is called to find the max height from the carousel images.
   *
   * @param slides contains the information of carousel slides.
   */
  getMaxHeight(slides) {
    const heightArr = [];
    slides.forEach((swiperSlide) => {
      heightArr.push(swiperSlide.fullImageAsset.height);
    });
    this.maxHeight = Math.max.apply(null, heightArr);
    this.customHeight = (this.maxHeight < ImageCarouselComponent.CAROUSEL_MAX_HEIGHT);
  }

  /**
   * It is used to set conditional custom height of the carousel.
   */
  getHeight() {
    return this.customHeight ? `${this.maxHeight}px` : '';
  }

  /**
   * It is called to update the carousel when slide is added or removed.
   *
   * @param event contains the information of content block.
   */
  updateCarouselSlides(event) {
    this.slides = event.detail.contentBlock.carouselImages;
    this.getMaxHeight(this.slides);
    this.swiper.update();
  }

  /**
   * Listen to the event on transition update for image carousel.So can be apply on carousel.
   *
   * @param event Event occurred
   */
  @HostListener('window:imageCarouselTransitionUpdate', ['$event'])
  onImageCarouselTransitionUpdate(event) {
    if (event.detail.blockId === this.blockId) {
      const { el } = this.swiper;
      if (el && (el as HTMLElement).clientWidth !== 0) {
        this.swiper.destroy(true, true);
        SwiperImageCarouselConfiguration.effect = event.detail.effect;
        this.swiper = new Swiper(`#${this.swiperContainerId}`, SwiperImageCarouselConfiguration);
        this.swiper.update();
        this.toggleClass(event.detail.effect);
        this.effect = event.detail.effect;
      }
    }
  }

  /**
   * Method to toggle class in image carousel
   * @param effectName
   */
  toggleClass(effectName) {
    const element = document.querySelector(`#${this.swiperContainerId}`);
    const swiperWrapperEle = element.querySelector('.swiper-wrapper');
    if (effectName === this.TRANSITION_EFFECT_CUBE) {
      swiperWrapperEle.classList.add('swiper-cube-shadow-margin');
      element.classList.add('cke-cropper-active');
    } else {
      swiperWrapperEle.classList.remove('swiper-cube-shadow-margin');
      element.classList.remove('cke-cropper-active');
    }

    if (!this.editable && effectName === this.TRANSITION_EFFECT_CUBE) {
      element.classList.remove('swiper-container-overflow');
    } else {
      element.classList.add('swiper-container-overflow');
    }
  }
}
